import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import ReactECharts from 'echarts-for-react';
import CountUp from 'react-countup';
import { ConfigContext } from '../../Context/ConfigContext';
import MainChart from './MainChart';
import { NavLink } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import './Dashboard.css';
import InstockOutstockChart from './InstockOutstockChart';
import PieChartComponent from './PieChart';

const Dashboard = () => {
    const { apiUrl, apiHeadersJson, store_id } = useContext(ConfigContext);
    const [chartType, setChartType] = useState('DESC');
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        orderStats: {
            totalOrders: 0,
            completedOrders: 0,
            canceledOrders: 0,
            pendingOrders: 0,
            excExpectedDate: 0,
            completedPercentage: 0,
            pendingPercentage: 0
        },
        productStats: {
            totalProducts: 0,
            productsInAlert: 0
        },
        monthlyData: [],
        stock: [],
        sale_summary: [],
    });

    // Date selection state
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // Month as number (1-12)

    const toast = useRef(null);
    const headers = apiHeadersJson;

    // Generate years for dropdown (current year and previous 5 years)
    const years = Array.from({ length: 6 }, (_, i) => currentDate.getFullYear() - i);

    // Months data as numbers with labels
    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const GetList = async () => {
        setDashboardLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/Reports/GetDashBoardStates`, {
                params: {
                    store_id,
                    month: selectedMonth,
                    year: selectedYear,
                    order: chartType,
                },
                headers
            });
            const { success, monthly_counts, order_states, product_states, message, stock, sale_summary } = response.data;

            if (success) {
                setDashboardData({
                    orderStats: {
                        totalOrders: order_states?.total_orders || 0,
                        completedOrders: order_states?.total_completed_orders || 0,
                        canceledOrders: order_states?.total_canceled_orders || 0,
                        pendingOrders: order_states?.total_pending_orders || 0,
                        excExpectedDate: order_states?.total_exc_expected_date || 0,
                        completedPercentage: parseFloat(order_states?.completed_percentage || 0),
                        pendingPercentage: parseFloat(order_states?.pending_percentage || 0)
                    },
                    productStats: {
                        totalProducts: product_states?.total_products || 0,
                        productsInAlert: product_states?.total_product_in_alert || 0
                    },
                    monthlyData: monthly_counts || [],
                    stock: stock || [],
                    sale_summary: sale_summary || []
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.log('error', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch dashboard data',
                life: 3000
            });
        } finally {
            setDashboardLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        GetList();
    };

    const handleYearChange = (e) => {
        setSelectedYear(parseInt(e.target.value));
        GetList();
    };

    const handleMonthChange = (e) => {
        setSelectedMonth(parseInt(e.target.value));
        GetList();
    };

    useEffect(() => {
        GetList();
    }, [store_id, chartType]);

    const renderCardSkeleton = () => (
        <div className="card stats-card shadow-sm">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <Skeleton width="120px" height="20px" className="mb-2" />
                        <Skeleton width="80px" height="30px" className="mb-3" />
                        <Skeleton width="150px" height="20px" />
                    </div>
                    <div className="icon-circle">
                        <Skeleton shape="circle" size="2.5rem" />
                    </div>
                </div>
                <div className="progress mt-4">
                    <Skeleton width="100%" height="6px" />
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <div className="container-fluid pb-5">
                {/* Date selection and refresh controls */}
                <div className="row mb-3">
                    <div className="col-md-6 d-flex align-items-center">
                        <h4 className="mb-0">Dashboard</h4>
                    </div>
                    <div className="col-md-6 d-flex justify-content-end">
                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="form-select form-select-sm w-auto"
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                disabled={dashboardLoading}
                            >
                                {months.map(month => (
                                    <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                            </select>

                            <select
                                className="form-select form-select-sm w-auto"
                                value={selectedYear}
                                onChange={handleYearChange}
                                disabled={dashboardLoading}
                            >
                                {years.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>

                            <Button
                                icon="pi pi-refresh"
                                className="p-button-sm p-button-text"
                                onClick={handleRefresh}
                                loading={refreshing}
                                tooltip="Refresh data"
                                tooltipOptions={{ position: 'bottom' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="row g-4 mt-3">
                    {/* Card 1: Total Orders */}
                    {dashboardLoading ? (
                        <div className="col-xl-3 col-md-6">
                            {renderCardSkeleton()}
                        </div>
                    ) : (
                        <div className="col-xl-3 col-md-6">
                            <div className="card stats-card card-hover-primary shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted text-uppercase fw-bold small">Total Orders</div>
                                            <div className="stat-value text-primary">
                                                <CountUp end={dashboardData.orderStats.totalOrders} duration={1} />
                                            </div>
                                            <div className="stat-change text-success">
                                                <i className="pi pi-arrow-up trend-icon" />
                                                <span>{dashboardData.orderStats.completedPercentage}% completed</span>
                                            </div>
                                        </div>
                                        <div className="icon-circle">
                                            <i className="pi pi-cart-arrow-down fs-4 text-primary" />
                                        </div>
                                    </div>
                                    <div className="progress mt-4">
                                        <div
                                            className="progress-bar bg-primary"
                                            role="progressbar"
                                            style={{ width: `${dashboardData.orderStats.completedPercentage}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Card 2: Completed Orders */}
                    {dashboardLoading ? (
                        <div className="col-xl-3 col-md-6">
                            {renderCardSkeleton()}
                        </div>
                    ) : (
                        <div className="col-xl-3 col-md-6">
                            <div className="card stats-card card-hover-success shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted text-uppercase fw-bold small">Completed Orders</div>
                                            <div className="stat-value text-success">
                                                <CountUp end={dashboardData.orderStats.completedOrders} duration={1} />
                                            </div>
                                            <div className="stat-change text-info">
                                                <i className="pi pi-undo trend-icon" />
                                                <span>{dashboardData.orderStats.canceledOrders} canceled orders</span>
                                            </div>
                                        </div>
                                        <div className="icon-circle">
                                            <i className="pi pi-list-check fs-4 text-success" />
                                        </div>
                                    </div>
                                    <div className="progress mt-4">
                                        <div
                                            className="progress-bar bg-success"
                                            role="progressbar"
                                            style={{ width: `${dashboardData.orderStats.completedPercentage}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Card 3: Pending Orders */}
                    {dashboardLoading ? (
                        <div className="col-xl-3 col-md-6">
                            {renderCardSkeleton()}
                        </div>
                    ) : (
                        <div className="col-xl-3 col-md-6">
                            <div className="card stats-card card-hover-warning shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted text-uppercase fw-bold small">Pending Orders</div>
                                            <div className="stat-value text-warning">
                                                <CountUp end={dashboardData.orderStats.pendingOrders} duration={1} />
                                            </div>
                                            <div className="stat-change text-warning">
                                                <i className="pi pi-stopwatch trend-icon" />
                                                <span>{dashboardData.orderStats.excExpectedDate} execute expected date</span>
                                            </div>
                                        </div>
                                        <div className="icon-circle">
                                            <i className="pi pi-hourglass fs-4 text-warning" />
                                        </div>
                                    </div>
                                    <div className="progress mt-4">
                                        <div
                                            className="progress-bar bg-warning"
                                            role="progressbar"
                                            style={{ width: `${dashboardData.orderStats.pendingPercentage}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Card 4: Products */}
                    {dashboardLoading ? (
                        <div className="col-xl-3 col-md-6">
                            {renderCardSkeleton()}
                        </div>
                    ) : (
                        <div className="col-xl-3 col-md-6">
                            <div className="card stats-card card-hover-info shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="text-muted text-uppercase fw-bold small">Products</div>
                                            <div className="stat-value text-info">
                                                <CountUp end={dashboardData.productStats.totalProducts} duration={1} />
                                            </div>
                                            <div className="stat-change text-danger">
                                                <i className="pi pi-exclamation-circle trend-icon" />
                                                <span>{dashboardData.productStats.productsInAlert} stock alert</span>
                                            </div>
                                        </div>
                                        <div className="icon-circle">
                                            <i className="pi pi-inbox fs-4 text-info" />
                                        </div>
                                    </div>
                                    <div className="progress mt-4">
                                        <div
                                            className="progress-bar bg-info"
                                            role="progressbar"
                                            style={{
                                                width: `${(dashboardData.productStats.productsInAlert / dashboardData.productStats.totalProducts) * 100}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Charts section */}
                {dashboardLoading ? (
                    <div className="row g-1 mt-3">
                        <div className="col-12">
                            <Skeleton width="100%" height="400px" />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="row g-1 mt-3">
                            <div className="col-12">
                                <MainChart monthly_counts={dashboardData.monthlyData} />
                            </div>
                        </div>

                        <div className="row g-1 mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Product Analytics</h5>
                                <select
                                    className="form-select form-select-sm ms-auto w-auto"
                                    value={chartType}
                                    onChange={(e) => setChartType(e.target.value)}
                                    disabled={dashboardLoading}
                                >
                                    <option value="DESC">Top 5</option>
                                    <option value="ASC">Last 5</option>
                                </select>
                            </div>

                            {dashboardLoading ? (
                                <>
                                    <div className="col-xl-6 col-md-12">
                                        <Skeleton width="100%" height="400px" />
                                    </div>
                                    <div className="col-xl-6 col-md-12">
                                        <Skeleton width="100%" height="400px" />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="col-xl-6 col-md-12">
                                        <InstockOutstockChart stock={dashboardData.stock} />
                                    </div>
                                    <div className="col-xl-6 col-md-12">
                                        <PieChartComponent chartType={chartType} sale_summary={dashboardData.sale_summary} />
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default Dashboard;